import React, { useState, useEffect } from 'react';
import { Office, officeApi } from '../services/api';
import OfficeForm from './OfficeForm';
import ConfirmDialog from './ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';
import './OfficeList.css';

interface OfficeListProps {
  onOfficeSelect?: (office: Office | null) => void;
  selectedOffice?: Office | null;
}

const OfficeList: React.FC<OfficeListProps> = ({ onOfficeSelect, selectedOffice: externalSelectedOffice }) => {
  const { canEdit } = useAuth();
  const [offices, setOffices] = useState<Office[]>([]);
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingOffice, setEditingOffice] = useState<Office | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; office: Office | null }>({
    isOpen: false,
    office: null
  });

  useEffect(() => {
    loadOffices();
  }, []);

  // Sync with external selection
  useEffect(() => {
    if (externalSelectedOffice) {
      // Find the office in our list by ID to ensure we have the same reference
      const matchingOffice = offices.find(o => o.id === externalSelectedOffice.id);
      if (matchingOffice) {
        setSelectedOffice(matchingOffice);
      } else {
        // If not found in list, use the external one
        setSelectedOffice(externalSelectedOffice);
      }
    }
  }, [externalSelectedOffice, offices]);

  const loadOffices = async () => {
    try {
      setLoading(true);
      const response = await officeApi.getAll();
      setOffices(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load offices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffice = () => {
    setEditingOffice(null);
    setShowForm(true);
  };

  const handleEditOffice = (office: Office) => {
    setEditingOffice(office);
    setShowForm(true);
  };

  const handleSelectOffice = (office: Office) => {
    setSelectedOffice(office);
    if (onOfficeSelect) {
      onOfficeSelect(office);
    }
  };

  const handleDeleteOffice = async (office: Office) => {
    setDeleteConfirm({ isOpen: true, office });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.office) return;
    
    try {
      await officeApi.delete(deleteConfirm.office.id);
      await loadOffices();
      if (selectedOffice?.id === deleteConfirm.office.id) {
        setSelectedOffice(null);
        if (onOfficeSelect) {
          onOfficeSelect(null);
        }
      }
      setDeleteConfirm({ isOpen: false, office: null });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete office');
      console.error(err);
      setDeleteConfirm({ isOpen: false, office: null });
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingOffice(null);
  };

  const handleFormSubmit = async () => {
    await loadOffices();
    handleFormClose();
  };

  if (loading) return <div className="loading">Loading offices...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="office-list">
      <div className="office-header">
        <h2>Offices</h2>
        {canEdit() && (
          <button className="btn btn-primary" onClick={handleCreateOffice}>
            Add Office
          </button>
        )}
      </div>

      <div className="office-grid">
        {offices.map((office) => (
          <div
            key={office.id}
            className={`office-card ${selectedOffice?.id === office.id ? 'selected' : ''}`}
            onClick={() => handleSelectOffice(office)}
          >
            <div className="office-card-header">
              <h3>{office.building} - {office.floor}</h3>
              {canEdit() && (
                <div className="office-actions">
                  <button
                    className="btn btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditOffice(office);
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteOffice(office);
                    }}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
            {office.description && (
              <p className="office-description">{office.description}</p>
            )}
            <div className="office-stats">
              <span>{office.ip_ranges.length} IP Range(s)</span>
              <span> | {office.public_ips?.length || 0} Public IP(s)</span>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <OfficeForm
          office={editingOffice}
          onClose={handleFormClose}
          onSubmit={handleFormSubmit}
        />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Office"
        message={`Are you sure you want to delete office "${deleteConfirm.office?.building} - ${deleteConfirm.office?.floor}"?\n\nThis will also delete all associated IP ranges and static IPs.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, office: null })}
        variant="danger"
      />
    </div>
  );
};

export default OfficeList;